import { z } from 'zod';

/** Content schemas (CONTENT.md §2). Content is code; these schemas validate it at definition time and in the check. */

export const CHALLENGE_TYPES = ['predict', 'code', 'bug-hunt', 'boss'] as const;
export const ChallengeTypeSchema = z.enum(CHALLENGE_TYPES);
export type ChallengeType = z.infer<typeof ChallengeTypeSchema>;

const contentId = z.string().regex(/^[a-z0-9-]+$/);

/** One frame of a visualizer: what each lane holds + a sentence announced to screen readers. */
export const VisualizerStepSchema = z.object({
  line: z.number().int().optional(),
  lanes: z.record(z.string(), z.array(z.string())),
  description: z.string(),
});
export type VisualizerStep = z.infer<typeof VisualizerStepSchema>;

export const VisualizerKindSchema = z.enum(['event-loop', 'scope', 'this']);
export type VisualizerKind = z.infer<typeof VisualizerKindSchema>;

export const VisualizerSchema = z.object({
  kind: VisualizerKindSchema,
  code: z.string().optional(),
  steps: z.array(VisualizerStepSchema).min(1),
});
export type Visualizer = z.infer<typeof VisualizerSchema>;

/**
 * Structural metadata of a challenge. Lives in each challenge's `meta.ts` so the track structure can be imported
 * without pulling instructions/tests into the main bundle.
 */
export const ChallengeMetaSchema = z.object({
  id: contentId,
  slug: contentId,
  title: z.string().max(60),
  type: ChallengeTypeSchema,
  estSeconds: z.number().int().min(15).max(1800),
  contentVersion: z.number().int().min(1),
  isGuest: z.boolean().default(false),
});
export type ChallengeMeta = z.infer<typeof ChallengeMetaSchema>;
export type ChallengeMetaInput = z.input<typeof ChallengeMetaSchema>;

const base = {
  id: contentId,
  slug: contentId,
  title: z.string().max(60),
  estSeconds: z.number().int().min(15).max(1800),
  contentVersion: z.number().int().min(1),
  isGuest: z.boolean().default(false),
  visualizer: VisualizerSchema.optional(),
};

export const PredictChallengeSchema = z.object({
  ...base,
  type: z.literal('predict'),
  code: z.string(),
  question: z.string(),
  options: z.array(z.string()).length(4),
  answer: z.number().int().min(0).max(3),
  explanation: z.string(),
});
export type PredictChallenge = z.infer<typeof PredictChallengeSchema>;

export const ExtraFileSchema = z.object({
  name: z.string().regex(/^[a-z0-9-]+\.js$/),
  code: z.string(),
  editable: z.boolean(),
});
export type ExtraFile = z.infer<typeof ExtraFileSchema>;

export const CodeChallengeSchema = z.object({
  ...base,
  type: z.enum(['code', 'bug-hunt', 'boss']),
  instructions: z.string(),
  starterCode: z.string(),
  extraFiles: z.array(ExtraFileSchema).optional(),
  tests: z.string(),
  hints: z.tuple([z.string(), z.string(), z.string()]),
});
export type CodeChallenge = z.infer<typeof CodeChallengeSchema>;

export const ChallengeSchema = z.discriminatedUnion('type', [PredictChallengeSchema, CodeChallengeSchema]);
export type Challenge = z.infer<typeof ChallengeSchema>;
export type ChallengeInput = z.input<typeof ChallengeSchema>;

export const RecapQuestionSchema = z
  .object({
    id: z.string(),
    prompt: z.string(),
    code: z.string().optional(),
    options: z.array(z.string()).min(2).max(4),
    answer: z.number().int().min(0),
    explanation: z.string(),
  })
  .refine((q) => q.answer < q.options.length, { message: 'answer out of range' });
export type RecapQuestion = z.infer<typeof RecapQuestionSchema>;

/** Chapter metadata + ordered challenge metadata. */
export type ChapterStructure = {
  id: string;
  slug: string;
  title: string;
  position: number;
  challenges: ChallengeMeta[];
};

export type TrackStatus = 'available' | 'coming_soon';

export type TrackStructureFull = {
  id: string;
  slug: string;
  title: string;
  shortTitle: string;
  position: number;
  status: TrackStatus;
  chapters: ChapterStructure[];
};

/** A loaded chapter: structure + full challenge content. */
export type LoadedChapter = Omit<ChapterStructure, 'challenges'> & { challenges: Challenge[] };
